import Utils from './utils.js';
import CoreUtils from '../../utils/coreUtils.js';
import { DEBUG_PRINT_DETAILED_COOK_INFO } from './constants.js';

export const PARAM_TYPE = 
{
	INTEGER:				1,
	FLOAT:					2,
	FILE:					3,
	GEOMETRY:				4,
	EXPRESSION:				5,
	STRING:					6,
	COLOR:					7,
	DROPDOWN:				9,
	BOOLEAN:				10,
	SPLINE:					11,
	BBOX:					12,
	DROPDOWN_3D_MODEL:		13,
	DROPDOWN_MATERIAL:		14,
	DROPDOWN_PATH:			15,
	DROPDOWN_PROCEDURAL:	16,
	MATERIAL_TWEAKS:		17,
	DROPDOWN_TEXTURE:		18,

	ANY:					9999
};

export const PARAM_UNITS = 
{
	NONE:					0,
	METERS:					1,
	PERCENT:				2,
	DEGREES:				3,
};
/*****************************************************************************/
export default class Connector
{
	constructor(eType, strName, pParentNode)
	{
		this.type = eType;
		this.name = strName;
		this.label = '';
		this.parentNode = pParentNode;

		this.isUserConnector = false;
	}

	static guessParamsFromName(strConnectorName, optExistingParms = null)
	{
		if(!optExistingParms)
			optExistingParms = { minValue: 0, maxValue: 3, units: PARAM_UNITS.METERS };

		if(!strConnectorName)
			return optExistingParms;

		if(strConnectorName.indexOf('rotation') >= 0)
		{
			optExistingParms.maxValue = 360;
			optExistingParms.initialValue = 0;
			optExistingParms.units = PARAM_UNITS.DEGREES;
		}

		return optExistingParms;
	}

	static guessTypeFromName(strConnectorName)
	{
		if(!strConnectorName)
			return PARAM_TYPE.FLOAT;

		if(strConnectorName.indexOf(`material`) >= 0)
			return PARAM_TYPE.DROPDOWN_MATERIAL;
		else if(strConnectorName.indexOf(`profile`) >= 0)
			return PARAM_TYPE.DROPDOWN_PATH;

		return PARAM_TYPE.FLOAT;
	}

	setIsUserConnector(bValue)
	{
		this.isUserConnector = bValue;
	}

	getIsUserConnector()
	{
		return this.isUserConnector;
	}

	// Do NOT ever, ever, ever call this function directly!
	renameUnsafe(strNewName)
	{
		this.name = strNewName;
	}

	getName()
	{
		return this.name;
	}

	setLabel(strLabel)
	{
		this.label = strLabel;
	}

	getLabel()
	{
		if(this.label.length > 0)
			return this.label;

		return CoreUtils.capitalizeFirstLetterAllWords(CoreUtils.replaceAll(this.name, '_', ' '))
	}

	getType()
	{
		return this.type;
	}

	setType(eType)
	{
		this.type = Number(eType);
	}

	getOwnNode()
	{
		return this.parentNode;
	}

	saveToObject()
	{
		let pRes = {
			't': this.type,
			'n': this.name,
		};

		if(this.isUserConnector)
			pRes['isUc'] = this.isUserConnector;

		if(this.label.length > 0)
			pRes['l'] = this.label;

		return pRes;
	}

	loadFromObject(pObj)
	{
		this.name = pObj['n'];
		this.type = pObj['t'];

		if(pObj['l'])
			this.label = pObj['l'];

		if(pObj['isUc'])
			this.isUserConnector = !!pObj['isUc'];
	}

	static evaluateExpression(strExpression, pInputNames, currentNodeInputResults, strCurrConnectorName)
	{
		let strExpr = CoreUtils.deepCopyString(strExpression);

		// We need to make sure the input names are sorted by the name length, otherwise
		// we're going to incorrect replace occurences of var and var2, for example:
		const pSortedNames = pInputNames.slice().sort((a, b) => { return b.length - a.length; });
		for(let strInputName of pSortedNames)
		{
			let currInputResult = currentNodeInputResults[strInputName];
			// We add quotes around a string and brackets around the evaluated result otherwise
			// in order to prevent cases where -$var becomes something like --5.0, which errors out.
			if(typeof currInputResult === 'string')
				currInputResult = `"${currInputResult}"`;
			// Note that we can't do general objects here because they may have circular references and
			// error out, and/or be large (both are the case for meshes). So we err on the side of caution
			// and only stringify spline objects.
			else if(Utils.getIsSplineObject(currInputResult))
				currInputResult = '(' + JSON.stringify(currInputResult) + ')';
			else
				currInputResult = '(' + currInputResult + ')';
			strExpr = CoreUtils.replaceAll(strExpr, '$' + strInputName, currInputResult);
		}

		let pRes;
		try
		{
			// eslint-disable-next-line
			pRes = eval('(' + strExpr + ')');
			if(DEBUG_PRINT_DETAILED_COOK_INFO)
				CoreUtils.log(`Nodes: Evaluating expression ${strExpression} RESULT = ${pRes}`);
			if(typeof pRes == 'number' && !isFinite(pRes))
				throw Error(`Expression results in infinity or NaN: ${strExpr}`);
		}
		catch(e)
		{
			console.error('Expression error: ' + e.message);
			pRes = null;			
			e.message = 'Error evaluating expression for connector ' + strCurrConnectorName + ': ' + e.message;
			throw(e);
		}
		return pRes;
	}

}
/*****************************************************************************/